import { getAssetPath } from '@/constants/configs';
import {
  defaultConfigs,
  getExtraBasicInfoQuestions as defaultGetExtraBasicInfoQuestions,
  resolveICBWebUrl,
} from '@/constants/tenant-configs/default';

import { QuestionCode } from '@/types/api/pep';
import {
  AppConfigurations,
  ExtraBasicInfoQuestions,
} from '@/types/app-configurations';
import { DocumentType } from '@/types/document';
import { Profession, User } from '@/types/user';

import { isBusinessPurposeQuestionEnabled, isMinorUser } from '@/utils/user';

export const bviConfigs: AppConfigurations = {
  ...defaultConfigs,
  links: {
    ...defaultConfigs.links,
    privacyPolicyURL: 'https://www.republicbankbvi.com/about/privacy-policy',
    termsAndConditionsURL:
      'https://republiconline.republictt.com/vg/legal.html',
    termsOfUseURL: getAssetPath('/tnc/republiconboard-tnc-bvi.pdf'),
    appTermsAndConditionsURL: getAssetPath('/tnc/republiconboard-tnc-bvi.pdf'),
    biometricConsentURL: getAssetPath(
      '/bio-consent/republiconboard-biometric-consent-bvi.pdf',
    ),
    icbWeb: resolveICBWebUrl('https://republiconlinevg.rfhl.com/RBVG.UI/'),
    icbIOS: 'https://apps.apple.com/us/app/republicmobile-vg/id1615104609',
    icbAndroid:
      'https://play.google.com/store/apps/details?id=vgicprod.com.republicapp',
    eFreeURL: 'https://www.republicbankbvi.com/personal/e-free',
    personalChequingURL:
      'https://www.republicbankbvi.com/personal/personal-chequing-account',
    savingsPlusURL:
      'https://www.republicbankbvi.com/personal/savings-plus-account',
  },
  defaultCountryCode: '1284',
  locale: 'en-VG',
  isoCode: 'VGB',
  dateFormat: 'dd/MM/yyyy',
  defaultCurrency: 'USD',
  idCaptureRequirement: 'idCardOrPassport',
  idDocumentTypes: [DocumentType.Passport, DocumentType.IdentityDriverLicense],
  showRiskSectorFlagQuestion: false,
  showSelfEmployedWithRiskSectorFlagQuestion: true,
  autoApproveUnemployedProfessions: [
    Profession.Pensioner,
    Profession.Housewife,
    Profession.Unemployed,
  ],
  existingCustomer: {
    maxAccountsPerType: 2,
  },
  jointAccountRelationshipOptions: [
    { value: 'PARENT', i18nKey: 'minorOthers.form.relationship.parent' },
    {
      value: 'LEGAL_GUARDIAN',
      i18nKey: 'minorOthers.form.relationship.legalGuardian',
    },
  ],
  childLivesWithRelationshipOptions: [
    {
      value: 'PARENT',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.parent',
    },
    {
      value: 'STEP_PARENT',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.stepParent',
    },
    {
      value: 'ADOPTIVE_PARENT',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.adoptiveParent',
    },
    {
      value: 'LEGAL_GUARDIAN',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.legalGuardian',
    },
    {
      value: 'FOSTER_PARENT',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.fosterParent',
    },
    {
      value: 'GRANDPARENT',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.grandparent',
    },
    {
      value: 'AUNT_UNCLE',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.auntUncle',
    },
    {
      value: 'OLDER_SIBLING',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.olderSibling',
    },
    {
      value: 'OTHER',
      i18nKey: 'minorPoA.childLivesWith.relationship.options.other',
    },
  ],
};

export const getExtraBasicInfoQuestions = (
  user: User,
): ExtraBasicInfoQuestions => {
  if (isMinorUser(user)) {
    return defaultGetExtraBasicInfoQuestions(user);
  }
  return {
    ...defaultGetExtraBasicInfoQuestions(user),
    [QuestionCode.AccountPurpose]: {
      enabled: isBusinessPurposeQuestionEnabled(user),
      question: 'questionDrawer.accountPurpose.question',
      priority: 0,
    },
  };
};
